/**
 * SpanishTranslationCTA - INLINE upgrade prompt for Spanish Translation add-on
 * 
 * CRITICAL: This is an INLINE PANEL, NOT a modal. It renders directly inside
 * the parent modal's content to avoid UI deadlocks from nested modals.
 * 
 * Button behaviors:
 * - "Continue without upgrade" → onContinue() → parent proceeds with save immediately
 * - "Upgrade to Spanish Translation" → onUpgrade() → navigate to upgrades page, no save
 * - "Use English instead" → onRemoveSpanishTechs() → remove Spanish techs from selection, toast, no save
 */

import { useNavigate } from "react-router-dom";
import { Globe } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";

export default function SpanishTranslationCTA({ 
  onContinue,            // Called when "Continue without upgrade" clicked - parent should proceed with save
  onUpgrade,             // Called when "Upgrade" clicked - parent should NOT save (component navigates)
  onRemoveSpanishTechs,  // Called when "Use English instead" clicked - parent removes Spanish techs, no save
}) {
  const navigate = useNavigate();

  // Handle "Continue without upgrade" - proceed with save
  const handleContinue = () => {
    if (onContinue) {
      onContinue();
    }
  };

  // Handle "Upgrade to Spanish Translation" - navigate, don't save
  const handleUpgrade = () => {
    if (onUpgrade) {
      onUpgrade();
    }
    // Navigate to upgrades page with feature context
    navigate("/settings/upgrades?feature=spanish_translation");
  };

  // Handle "Use English instead" - remove Spanish techs, show toast, don't save
  const handleUseEnglish = () => {
    if (onRemoveSpanishTechs) {
      onRemoveSpanishTechs();
    }
    toast.info("Choose an English-speaking technician, or upgrade for Spanish Translation.");
  };

  return (
    <div 
      className="p-4 bg-orange-50 border border-orange-200 rounded-md"
      data-testid="spanish-cta-panel"
    >
      {/* Header with icon */}
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 bg-white rounded-full border border-orange-200">
          <Globe className="w-5 h-5 text-[#FF6600]" />
        </div>
        <h4 
          className="text-sm font-semibold text-gray-900" 
          data-testid="spanish-cta-title"
        >
          Spanish Translation is an add-on
        </h4>
      </div>

      {/* Description */}
      <p className="text-sm text-gray-600 mb-4 leading-relaxed">
        This workflow requires automatic translation for Spanish-speaking technicians.
      </p>

      {/* Action Buttons - stacked for clarity */}
      <div className="flex flex-col gap-2">
        <Button
          type="button"
          onClick={handleContinue}
          variant="outline"
          className="w-full h-10 border-2 font-medium"
          data-testid="spanish-cta-continue-btn"
        >
          Continue without upgrade
        </Button>
        
        <Button
          type="button"
          onClick={handleUpgrade}
          className="w-full h-10 bg-[#FF6600] hover:bg-[#E55C00] text-white font-medium"
          data-testid="spanish-cta-upgrade-btn"
        >
          Upgrade to Spanish Translation
        </Button>
        
        <Button
          type="button"
          variant="ghost"
          onClick={handleUseEnglish}
          className="w-full h-9 text-gray-600 hover:text-gray-800 text-sm"
          data-testid="spanish-cta-english-btn"
        >
          Use English instead
        </Button>
      </div>
    </div>
  );
}
